import React, { ReactNode } from "react";
import "./AlertIndicator.scss";
import ClassNames from "classnames";
import { IconButton } from "../../controls";
import Tooltip from "../Tooltip/Tooltip";
import { useIcon } from "../../assets/useIcon";
import { useCopyToClipboard } from "../../../hooks";

export const alertTypes = {
  WARNING: "warning",
  SUCCESS: "success",
  INFO: "info",
  ERROR: "error",
  GENERAL: "general",
  EXTRA_INFO: "extra-info",
  AI: "ai",
} as const;

export type AlertType = (typeof alertTypes)[keyof typeof alertTypes];

interface AlertIndicatorWithHelp {
  id?: string;
  content?: ReactNode;
}

export interface AlertIndicatorProps {
  wrapperClassName?: string;
  content?: ReactNode;
  title?: string;
  hasMaxWidth?: boolean;
  borderless?: boolean;
  showClose?: boolean;
  onClose?: () => void;
  type?: AlertType;
  "data-testid"?: string;
  withHelp?: AlertIndicatorWithHelp;
  svgIcon?: string | false;
  enableCopy?: boolean;
  contentRight?: ReactNode;
}

const alertIconsEnum: Partial<Record<AlertType, string>> = {
  [alertTypes.WARNING]: "seem-icon-warning",
  [alertTypes.SUCCESS]: "seem-icon-info",
  [alertTypes.INFO]: "seem-icon-info",
  [alertTypes.ERROR]: "seem-icon-error",
  [alertTypes.EXTRA_INFO]: "seem-icon-new-actions",
};

const AlertIndicator: React.FC<AlertIndicatorProps> = ({
  wrapperClassName = "",
  content = null,
  title = "",
  hasMaxWidth = true, // to support backwards-compatibility
  borderless = false,
  showClose = false,
  onClose = () => {},
  type = alertTypes.INFO,
  "data-testid": dataTestId,
  withHelp,
  svgIcon = false,
  enableCopy = false,
  contentRight = null,
}) => {
  const classNameWrapper = ClassNames(
    "alert-indicator-container",
    `alert-indicator-container-${type}`,
    wrapperClassName,
    {
      "has-content": content,
      "has-title": !!title,
      "has-showClose": showClose,
      "has-max-width": hasMaxWidth,
      borderless: borderless,
    }
  );
  const { getSvg } = useIcon();

  const { copyLabel, copyText } = useCopyToClipboard();

  return (
    <div className={classNameWrapper}>
      <div className={`alert-indicator-offset`} />
      <div className={"alert-indicator-wrap"}>
        <div className={`alert-indicator-wrap-text-and-icon`}>
          {!!svgIcon ? (
            <img
              src={getSvg(svgIcon)}
              alt={svgIcon}
              className={`alert-indicator-wrap-svg-icon`}
            />
          ) : (
            type !== alertTypes.GENERAL &&
            type !== alertTypes.AI && (
              <i
                className={`alert-indicator-icon seem-icon ${alertIconsEnum[type]}`}
              />
            )
          )}
          <div className={"alert-indicator-text"}>
            {title && (
              <div className="alert-indicator-title">
                {title}

                {!!withHelp?.content && (
                  <div className="input-help-text">
                    <Tooltip
                      type="info"
                      content={withHelp.content}
                      className="input-help-tooltip"
                    >
                      <i className={`seem-icon seem-icon-help`} />
                    </Tooltip>
                  </div>
                )}
              </div>
            )}
            <div
              className={`alert-indicator-content ${title ? "has-title" : ""}`}
            >
              {content}
            </div>
          </div>
        </div>
        {(showClose || enableCopy || contentRight) && (
          <div className="alert-indicator-right">
            <div className="alert-indicator-buttons-container">
              {showClose && (
                <IconButton
                  wrapperClassName={"close-alert-indicator-wrapper"}
                  data-testid={`close-alert-indicator-${dataTestId}`}
                  size={"xs"}
                  withTooltip={{ content: "Dismiss" }}
                  isIcon="seem-icon-close"
                  type={`button`}
                  title={"Close"}
                  onClick={onClose}
                />
              )}
              {!!enableCopy && (
                <IconButton
                  wrapperClassName={"copy-alert-wrapper"}
                  data-testid={`copy-${dataTestId}`}
                  type="button"
                  onClick={() => {
                    copyText((content as string) || "");
                  }}
                  withTooltip={{ content: copyLabel }}
                  size={"s"}
                  buttonStyle={"secondary"}
                  isIcon={"seem-icon-copy"}
                />
              )}
            </div>
            {contentRight && (
              <div className="alert-indicator-right-content">
                {contentRight}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AlertIndicator;
